/**
 * useMediaUpload – uploads files through the Farmly media pipeline.
 *
 * Handles:
 * - Validation (type, size, HEIC detection)
 * - Client-side variant creation (320/640/1280 WebP)
 * - Upload to private-media (draft) or public-media
 * - DB row creation in media_assets
 * - Full observability: PostHog + Sentry breadcrumbs + server fallback
 *
 * RULES:
 * - No ownerId → bucket='private-media', path=tmp/{userId}/{draftSessionId}/…
 *   draftSessionId is REQUIRED in this case.
 * - ownerId present → bucket='public-media', path={ownerType}/{ownerId}/{kind}/…
 *   draftSessionId MUST be null in this case.
 */

import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { fileSizeBucket, mimeGroup } from '@/lib/analytics-helpers';
import { shouldReportToSentry } from '@/lib/monitoring/sentryGate';

import {
  validateFile,
  downscaleToMaxPixels,
  createWebpVariants,
  MediaUploadError,
} from '@/lib/media/imageProcessing';
import { compressImageFile } from '@/lib/media/compressImage';
import { assessImageSuitability, measureImageFile } from '@/lib/media/assessImageSuitability';
import { defaultImageIntent } from '@/lib/media/imageIntent';

import {
  buildTmpPath,
  buildPublicPath,
  type MediaBucket,
  type MediaKind,
  type OwnerType,
  type VariantWidth,
} from '@/lib/media/mediaTypes';

import { createMediaAssetRow, uploadVariantBlob } from '@/lib/media/mediaApi';
import type { MediaAsset } from '@/lib/media/mediaTypes';

import {
  isHeicFile,
  toStructuredUploadError,
  type UploadStage,
} from '@/lib/media/uploadErrors';

import {
  generateUploadRequestId,
  buildUploadContext,
  addStageBreadcrumb,
  reportUploadFailureToSentry,
  logUploadFailureToServer,
} from '@/lib/media/uploadTelemetry';

/** Timeout per file upload (all variants), in ms. */
const UPLOAD_TIMEOUT_MS = 60_000;

interface UseMediaUploadOptions {
  ownerType: OwnerType;
  ownerId?: string | null;       // if present => public-media
  draftSessionId?: string;        // required if ownerId is missing
  kind: MediaKind;
  maxFiles?: number;
  seasonKey?: string | null;      // set to tag assets with a season
}

interface UploadState {
  isUploading: boolean;
  progress: number; // 0-100
  errors: string[];
}

export function useMediaUpload(opts: UseMediaUploadOptions) {
  const [state, setState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    errors: [],
  });

  const abortRef = useRef(false);

  const uploadFiles = useCallback(
    async (files: File[]): Promise<MediaAsset[]> => {
      // Block uploads in support preview mode
      if ((await import('@/lib/supportPreview')).isSupportPreviewActive()) {
        toast.error('Supportläge', { description: 'Uppladdning är inte tillåten i supportläge.' });
        return [];
      }
      abortRef.current = false;

      const isDraft = !opts.ownerId;
      const bucket: MediaBucket = isDraft ? 'private-media' : 'public-media';

      // Draft requires a caller-provided draftSessionId. Never generate one here.
      const sessionId: string | null = isDraft ? (opts.draftSessionId ?? null) : null;

      console.log('[media] upload start', {
        ownerType: opts.ownerType,
        ownerId: opts.ownerId ?? null,
        draftSessionId: opts.draftSessionId ?? null,
        kind: opts.kind,
        isDraft,
        bucket,
        fileCount: files.length,
      });

      if (isDraft && !sessionId) {
        const msg =
          'Uppladdning kräver ett draftSessionId när producenten inte finns ännu.';
        console.error('[media] missing draftSessionId for draft upload');
        toast.error('Uppladdningsfel', { description: msg });
        setState({ isUploading: false, progress: 0, errors: [msg] });
        return [];
      }

      setState({ isUploading: true, progress: 0, errors: [] });

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) {
        const msg = 'Du måste vara inloggad för att ladda upp bilder.';
        toast.error('Inte inloggad', { description: msg });
        setState({ isUploading: false, progress: 0, errors: [msg] });
        return [];
      }

      const maxFiles = opts.maxFiles ?? 12;
      const filesToProcess = files.slice(0, maxFiles);

      const results: MediaAsset[] = [];
      const errors: string[] = [];
      const total = filesToProcess.length;

      for (let i = 0; i < filesToProcess.length; i++) {
        if (abortRef.current) break;

        const file = filesToProcess[i];
        const requestId = generateUploadRequestId();
        const startTime = Date.now();
        let currentStage: UploadStage = 'validate';

        const ctx = buildUploadContext(requestId, file, {
          ownerType: opts.ownerType,
          ownerId: opts.ownerId,
          draftSessionId: opts.draftSessionId,
          kind: opts.kind,
        });

        const analyticsBase = {
          owner_type: opts.ownerType,
          owner_id: opts.ownerId || opts.draftSessionId || 'unknown',
          file_size_bucket: fileSizeBucket(file.size) as "<1mb" | "1-5mb" | "5-20mb" | "20mb+",
          mime_group: mimeGroup(file.type || 'unknown') as "image" | "video" | "other",
        };

        try {
          // ── Track start ──
          track('media_upload_started', analyticsBase);
          addStageBreadcrumb('validate', ctx);

          // Log file details for debugging (especially Samsung/Android)
          console.log('[media] file details', {
            request_id: requestId,
            name: file.name,
            type: file.type || '(empty)',
            size: file.size,
            platform: ctx.platform,
            userAgent: ctx.user_agent,
          });

          // ── Stage: validate ──
          // Image quality / resolution check
          try {
            const dims = await measureImageFile(file);
            const intent = defaultImageIntent(opts.ownerType, opts.kind === 'cover' ? 'cover' : 'gallery');
            const suitability = assessImageSuitability(dims.width, dims.height, intent);
            if (suitability.suitability === 'block') {
              throw new MediaUploadError(suitability.message ?? 'Bilden är för liten.');
            }
            if (suitability.suitability === 'warn') {
              toast.warning(suitability.message ?? 'Bilden är lite liten.');
            }
          } catch (dimErr) {
            if (dimErr instanceof MediaUploadError) throw dimErr;
            // If we can't measure, log and continue — pipeline will still process
            console.warn('[media] could not measure image dimensions', { request_id: requestId, error: dimErr });
          }

          // Check for HEIC before standard validation
          if (isHeicFile(file)) {
            throw new MediaUploadError(
              'Bilden är i HEIC-format som inte stöds i webbläsaren. Välj en annan bild eller ändra kamerainställning till JPEG.',
            );
          }

          // If file.type is empty (some Android browsers), try to infer
          if (!file.type) {
            const ext = file.name.split('.').pop()?.toLowerCase();
            const inferredOk = ['jpg', 'jpeg', 'png', 'webp', 'avif'].includes(ext ?? '');
            if (!inferredOk) {
              throw new MediaUploadError(
                `Kunde inte avgöra filtyp för "${file.name}". Välj en JPG, PNG eller WebP.`,
              );
            }
            // If extension is OK but type empty, create a new File with correct type
            const mimeMap: Record<string, string> = {
              jpg: 'image/jpeg', jpeg: 'image/jpeg',
              png: 'image/png', webp: 'image/webp', avif: 'image/avif',
            };
            if (ext && mimeMap[ext]) {
              const corrected = new File([file], file.name, { type: mimeMap[ext] });
              // Replace file reference for subsequent stages
              Object.defineProperty(corrected, '_originalFile', { value: file });
              // Use corrected file going forward
              filesToProcess[i] = corrected;
            }
          }

          const activeFile = filesToProcess[i];

          // Standard validation
          currentStage = 'validate';
          validateFile(activeFile);

          // ── Stage: compress ──
          currentStage = 'compress';
          addStageBreadcrumb('compress', ctx);
          const compressed = await compressImageFile(activeFile);

          // ── Stage: process (downscale + variants) ──
          currentStage = 'process';
          addStageBreadcrumb('process', ctx);
          const { img, originalWidth, originalHeight } = await downscaleToMaxPixels(compressed);
          const variants = await createWebpVariants(img);
          console.log('[media] variants', { request_id: requestId, widths: variants.map(v => v.width) });

          // ── Stage: upload_storage ──
          currentStage = 'upload_storage';
          addStageBreadcrumb('upload_storage', ctx, { variant_count: variants.length });

          const assetId = crypto.randomUUID();
          const paths: Partial<Record<VariantWidth, string>> = {};

          // Create an AbortController for timeout
          const abortController = new AbortController();
          const timeoutId = setTimeout(() => abortController.abort(), UPLOAD_TIMEOUT_MS);

          try {
            for (const variant of variants) {
              if (abortRef.current) {
                abortController.abort();
                break;
              }

              const path = isDraft
                ? buildTmpPath(userId, sessionId!, assetId, variant.width)
                : buildPublicPath(
                  opts.ownerType,
                  opts.ownerId!,
                  opts.kind,
                  assetId,
                  variant.width,
                );

              console.log('[media] storage upload', { request_id: requestId, bucket, path, width: variant.width });

              // Check if aborted before upload
              if (abortController.signal.aborted) {
                throw new MediaUploadError('Uppladdningen tog för lång tid. Försök igen.');
              }

              await uploadVariantBlob(bucket, path, variant.blob);
              paths[variant.width] = path;
            }
          } finally {
            clearTimeout(timeoutId);
          }

          // ── Stage: db_insert ──
          currentStage = 'db_insert';
          addStageBreadcrumb('db_insert', ctx);

          const asset = await createMediaAssetRow({
            ownerType: opts.ownerType,
            ownerId: isDraft ? null : opts.ownerId!,
            draftSessionId: isDraft ? sessionId! : null,
            bucket,
            kind: opts.kind,
            mime: 'image/webp',
            width: originalWidth,
            height: originalHeight,
            sizeBytes: file.size,
            sortOrder: i,
            paths,
            seasonKey: opts.seasonKey,
          });

          console.log('[media] db row', {
            request_id: requestId,
            id: asset.id,
            bucket: asset.bucket,
            ownerId: asset.owner_id,
            draftSessionId: asset.draft_session_id,
            path_320: asset.path_320,
          });

          // ── Done ──
          currentStage = 'done';
          addStageBreadcrumb('done', ctx, { duration_ms: Date.now() - startTime });
          track('media_upload_succeeded', analyticsBase);
          results.push(asset);
        } catch (err: unknown) {
          const structured = toStructuredUploadError(err, currentStage);
          const durationMs = Date.now() - startTime;

          console.error('[media] upload failed', {
            request_id: requestId,
            stage: currentStage,
            error_kind: structured.kind,
            fileName: file.name,
            fileType: file.type || '(empty)',
            fileSize: file.size,
            platform: ctx.platform,
            error: structured.originalMessage,
            duration_ms: durationMs,
          });

          // PostHog
          track('media_upload_failed', {
            ...analyticsBase,
            failure_reason: structured.kind,
          });

          // Sentry (only for unexpected failures)
          if (shouldReportToSentry('media_upload', structured.kind)) {
            reportUploadFailureToSentry(err, currentStage, structured.kind, ctx);
          }

          // Server fallback logging (best-effort)
          logUploadFailureToServer(
            currentStage,
            structured.kind,
            structured.originalMessage ?? 'unknown',
            ctx,
            durationMs,
          );

          // Show context-appropriate toast
          if (structured.stage === 'validate') {
            // Validation errors: show specific reason, no "försök igen"
            toast.warning(structured.originalMessage ?? structured.message_sv, {
              duration: 6000,
            });
          } else {
            toast.error('Uppladdningsfel', {
              description: structured.message_sv,
            });
          }
          errors.push(structured.message_sv);
        }

        setState((prev) => ({
          ...prev,
          progress: Math.round(((i + 1) / total) * 100),
          errors,
        }));
      }

      setState({ isUploading: false, progress: 100, errors });
      return results;
    },
    [opts.ownerType, opts.ownerId, opts.draftSessionId, opts.kind, opts.maxFiles, opts.seasonKey],
  );

  const cancel = useCallback(() => {
    abortRef.current = true;
  }, []);

  return {
    ...state,
    uploadFiles,
    cancel,
  };
}
